<template>
  <div class="hd-SearchField">
    <form ref="form" :action="path" method="get" class="hd-SearchField_Form position-relative">
      <select
        v-if="initialVendor"
        :value="vendorPermalink"
        @change="setVendorSelect"
        class="inline-select d-none d-sm-block"
      >
        <option :value="initialVendor.permalink">{{ initialVendor.name }}</option>
        <option value="">{{ t.all }}</option>
      </select>
      <input
        v-if="initialVendor && vendorPermalink"
        :value="vendorPermalink"
        type="hidden"
        name="vendor_permalink"
      />
      <vue-autosuggest
        :suggestions="suggestions"
        v-model="currentValue"
        :input-props="{
          class: 'form-control hd-SearchField_InputField',
          id: 'autosuggest__input',
          name: 'keywords',
          placeholder: t.searchbar_placeholder,
        }"
        :getSuggestionValue="getSuggestionValue"
        @input="onInputChange"
        @click="triggerSearchOverlay"
        @selected="selectHandler"
        @closed="clearSearchResults"
        :component-attr-id-autosuggest="id"
        class="hd-SearchField_InputWrapper w-100"
      >
        <template slot-scope="{ suggestion }">
          <img :src="suggestion.item.images[0].url" class="rounded hd-SearchField_ResultImage" />
          <span>{{ getSuggestionValue(suggestion) }}</span>
        </template>
      </vue-autosuggest>
      <button class="btn hd-SearchField_SearchBtn" type="submit">
        <SearchIcon class="svg-24 svg-fill-dark" />
      </button>
    </form>
    <portal to="overlay-portal">
      <Overlay v-if="searchOpen" is-full-mobile class="d-sm-none">
        <SearchbarMobileOverlay
          @changeCurrentValue="changeCurrentValue"
          @setVendorPermalink="selectVendor"
          @select="selectHandler"
          :initialVendor="initialVendor"
          :placeholder="filledPlaceholder"
          :currentValue="currentValue"
          :suggestions="suggestions"
          :t="t"
        />
      </Overlay>
    </portal>
  </div>
</template>

<script>
import SearchbarMobileOverlay from "./SearchbarMobileOverlay";
import Overlay from "./Overlay";
import { VueAutosuggest } from "vue-autosuggest";
import { mapGetters, mapActions } from "vuex";
import SearchIcon from "../../icons/atalanda/search.svg";
import { toPixel } from "../utils/BreakPointMapper";
import debounce from "lodash/debounce";

export default {
  name: "Searchbar",
  components: {
    VueAutosuggest,
    SearchIcon,
    SearchbarMobileOverlay,
    Overlay,
  },
  props: {
    path: {
      type: String,
      required: true,
    },
    initialValue: {
      type: String,
      default: "",
      required: false,
    },
    initialVendor: {
      type: Object,
      default: () => {},
      required: false,
    },
    id: {
      type: String,
      required: false,
      default: "searchbar",
    },
    city: {
      type: String,
      required: false,
      default: "",
    },
    autocomplete: {
      type: Boolean,
      default: true,
    },
    t: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      currentValue: this.initialValue,
      vendorPermalink: this.initialVendor?.permalink,
    };
  },
  computed: {
    ...mapGetters({
      searchResults: "productSearch/getSearchResults",
      currentLocale: "getCurrentLocale",
      searchOpen: "getSearchOpenState",
    }),
    suggestions() {
      return [{ label: this.filledProductsTitle, data: this.searchResults.slice(0, 10) }];
    },
    filledPlaceholder() {
      return this.vendorPermalink ? this.t.vendor_placeholder : this.t.searchbar_placeholder;
    },
    filledProductsTitle() {
      return this.vendorPermalink ? this.t.products_from_vendor : this.t.products_from_all;
    },
  },
  methods: {
    ...mapActions({
      fetchSearchResults: "productSearch/fetchSearchResults",
      clearSearchResults: "productSearch/clearSearchResults",
      toggleSearchState: "toggleSearchState",
    }),
    triggerSearchOverlay() {
      if (window.innerWidth < toPixel("sm")) {
        this.toggleSearchState();
      }
    },
    setVendorSelect(e) {
      this.vendorPermalink = e.target.value;
      if (this.autocomplete) this.fetchResults(this.currentValue);
    },
    selectVendor(permalink) {
      this.vendorPermalink = permalink;
      this.$nextTick(() => {
        this.$refs.form.submit();
      });
    },
    changeCurrentValue(value) {
      this.currentValue = value;
      this.onInputChange(value);
    },
    onInputChange(value) {
      if (this.autocomplete) this.fetchResults(value);
    },
    fetchResults: debounce(function(value) {
      let searchQuery = value.trim();
      if (searchQuery.length > 2) {
        this.fetchSearchResults({
          keywords: searchQuery,
          city: this.city,
          vendor: this.vendorPermalink ? this.initialVendor : undefined,
        });
      }
    }, 500),
    selectHandler(suggestionItem) {
      if (suggestionItem?.item) {
        window.location.assign(`${this.path}/${suggestionItem.item.permalink}`);
      } else {
        this.$refs.form.submit();
      }
    },
    getSuggestionValue(suggestion) {
      return suggestion.item.name[this.currentLocale] || suggestion.item.name.de;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~styles/global";

.inline-select {
  font-weight: $font-weight-bold;
  font-size: $font-size-sm;
  height: auto;
  color: $gray-900;
  width: $width-select-searchfield;
  border: 0;
  outline: 0;
  padding: 0 $spacer-2;
  padding-right: $spacer;
  text-overflow: ellipsis;
  appearance: none;
  background-color: $white;
  background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M5.29289 8.29289C5.68342 7.90237 6.31658 7.90237 6.70711 8.29289L12 13.5858L17.2929 8.29289C17.6834 7.90237 18.3166 7.90237 18.7071 8.29289C19.0976 8.68342 19.0976 9.31658 18.7071 9.70711L12.7071 15.7071C12.3166 16.0976 11.6834 16.0976 11.2929 15.7071L5.29289 9.70711C4.90237 9.31658 4.90237 8.68342 5.29289 8.29289Z' fill='currentColor'/%3E%3C/svg%3E%0A");
  background-repeat: no-repeat, repeat;
  background-position: right 0 top 50%, 0 0;
  background-size: $font-size-base auto, 100%;
}
</style>
